import React, { useRef } from 'react';

import { IconUpload } from '@iwoca/icons';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { Trans } from '@lingui/react/macro';
import { v4 } from 'uuid';

import styles from './UploadButton.module.css';
import {
  trackDocumentUploadClicked,
  trackDocumentUploaded,
} from './UploadButton.tracking.helper';
import { fetchSaveUploadedDocuments } from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { TDocumentTypeDE } from '../../../lapiOverrideTypesDE';
import { Button } from '../../Button/Button';

export type TUploadRejectReasonCode =
  | 'ILLEGIBLE'
  | 'PASSWORD_PROTECTED'
  | 'MISSING_TRANSACTIONS'
  | 'MISSING_ACCOUNT_DETAILS'
  | 'MISSING_BALANCES'
  | 'INCOMPLETE_REFERENCES'
  | 'OTHER'
  | 'DUPLICATE'
  | 'UNSUPPORTED_EXTENSION'
  | 'UNSUPPORTED_FORMAT'
  | 'NO_RELEVANT_DATA'
  | 'OUTDATED_INFORMATION';

type TProps = {
  documentType?:
    | PostDocumentUploadResponse['data']['document_type']
    | TDocumentTypeDE;
  setFileLoading: (id: string, name: string) => void;
  setFileSuccess: (id: string) => void;
  setFileError: (id: string, error: { code: string; detail: string }) => void;
  setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
  isOptional?: boolean;
  disabled?: boolean;
};

const UploadButton = ({
  documentType = 'other',
  setFileLoading,
  setFileSuccess,
  setFileError,
  setFileReject,
  isOptional,
  disabled,
}: TProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const stateKey = useContextStateKey();

  const onButtonClick = () => {
    inputFileRef?.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !stateKey) {
      return;
    }

    const files = Array.from(e.target.files);

    await Promise.all(
      files.map(async (file) => {
        const uuid = v4();
        setFileLoading(uuid, file.name);
        const body = new FormData();

        body.append('document', file);
        body.append('document_type', documentType);

        const response = await fetchSaveUploadedDocuments({
          stateKey,
          body,
        });

        if (response?.errors?.[0]) {
          setFileError(uuid, response.errors[0]);
        } else if (response?.attributes?.status === 'rejected') {
          setFileReject(uuid, response?.attributes?.reject_reason);
        } else {
          trackDocumentUploaded(documentType);
          setFileSuccess(uuid);
        }
      }),
    );

    // Reset input value to allow same file upload to fire onChange.
    e.target.value = '';
  };

  return (
    <>
      <Button
        className={styles.button}
        variant={isOptional ? 'tertiary' : 'secondary'}
        onClick={onButtonClick}
        disabled={disabled}
      >
        <Trans>Upload</Trans> <IconUpload />
      </Button>
      <input
        data-testid={`upload-input-${documentType}`}
        multiple
        hidden
        type="file"
        onClick={() => trackDocumentUploadClicked(documentType)}
        ref={inputFileRef}
        accept=".jpg, .jpeg, .png, .pdf"
        onChange={handleChange}
        className="fs-mask"
        disabled={disabled}
      />
    </>
  );
};

export default UploadButton;
